import {
  Grid,
  MenuItem,
  SelectProps,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import SelectField from "components/Forms/SelectField";
import { DELIVERY_STATUS_CUTOFF, Filters } from "pages/EmailFlowLogs/types";
import { useTranslation } from "react-i18next";

export const deliveryStatuses = [
  "DELIVERED",
  "BOUNCED_EXTERNAL",
  "WITH_RETRIES",
  "TIMED_OUT",
];

interface Props {
  form: Filters;
  onStatusChange: SelectProps["onChange"];
  onErrorCodesChange: (errors: Filters["errorCodes"]) => void;
  tab: "SEARCH" | "REPORTS";
}

export default function StatusSelector(props: Props) {
  const { t } = useTranslation("emailFlowLogs");
  const { form, onStatusChange, onErrorCodesChange } = props;
  const deliveryFiltersDisabled = form.fromDate < DELIVERY_STATUS_CUTOFF;

  function handleErrorTypes(_, value: string[]) {
    const errors = value as Filters["errorCodes"];
    onErrorCodesChange(errors);
  }

  return (
    <Grid container xs={12} style={{ marginBottom: "20px" }}>
      <Grid item xs={6} style={{ paddingRight: "10px" }}>
        <SelectField
          name="status"
          label={t("search.status.label")}
          options={statusOptions}
          allowEmpty
          emptyOption={t("search.status.option.ALL")}
          value={form.status}
          onChange={onStatusChange}
          renderOption={opt => {
            const isDisabled =
              deliveryFiltersDisabled && deliveryStatuses.includes(opt);

            if (isDisabled) {
              return (
                <Tooltip
                  key={opt}
                  title={t("search.status.deliveryFilterDisabled")}
                  placement="right"
                >
                  <span>
                    <MenuItem value={opt} disabled>
                      {t(`search.status.option.${opt}`) as string}
                    </MenuItem>
                  </span>
                </Tooltip>
              );
            }

            return (
              <MenuItem key={opt} value={opt}>
                {t(`search.status.option.${opt}`) as string}
              </MenuItem>
            );
          }}
        />
      </Grid>
      {showErrorCodesSelector(form.status) && (
        <Grid item xs={6} style={{ paddingLeft: "10px" }}>
          <Autocomplete
            multiple
            options={errorCodesByStatus.get(form.status) || []}
            limitTags={3}
            filterSelectedOptions
            getOptionLabel={opt => t(`search.errorCodes.option.${opt}`)}
            getOptionSelected={(opt, selected) => opt === selected}
            ChipProps={{ size: "small" }}
            value={form?.errorCodes || []}
            onChange={handleErrorTypes}
            getLimitTagsText={n => (
              <Typography
                style={{ marginLeft: 5 }}
                variant="caption"
              >{`+${n}`}</Typography>
            )}
            renderInput={props => (
              <TextField
                label={t("search.errorCodes.label")}
                variant="outlined"
                fullWidth
                {...props}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  );
}

function showErrorCodesSelector(status: Filters["status"]) {
  const allowedStatuses = ["WITH_STORAGE_ERRORS", "WITH_RULE_ERRORS"];
  const options = errorCodesByStatus.get(status) || [];
  return allowedStatuses.includes(status) && Boolean(options.length);
}

const statusOptions = [
  "DELIVERED",
  "TIMED_OUT",
  "DROPPED",
  "REJECTED",
  "QUARANTINE",
  "BOUNCED_EXTERNAL",
  "BOUNCED_INTERNAL",
  "WITH_RETRIES",
  "WITH_ERRORS",
  "WITH_RULE_ERRORS",
  "WITH_STORAGE_ERRORS",
];

const errorCodesByStatus = new Map<Filters["status"], Filters["errorCodes"]>([
  [
    "WITH_STORAGE_ERRORS",
    [
      "STORAGE_SHARE_FILE_ERROR",
      "STORAGE_COLLABORATE_ERROR",
      "STORAGE_ERROR_STORING_FILE",
      "STORAGE_SET_MXHERO_METADATA_ERROR",
      "STORAGE_ERROR_SET_METADATA",
      "ASYNC_STORAGE_INSTANCE_FOR_USER",
      "STORAGE_ATTACHMENT_EXTRACT_ERROR",
      "STORAGE_MAIL_EXTRACT_ERROR",
    ],
  ],
  ["WITH_RULE_ERRORS", []],
]);
